import assert from '../style-spec/util/assert';
import {CollisionGrid} from './collision_grid';
import {comparePriority} from './global_placement_priority';
import {VariantPlacementResult} from './placement_debug';
import {SymbolVariantVisibility} from './types';

import type {Geometry, GeometryElement} from './geometry';
import type {GlobalPlacementPriority} from './global_placement_priority';
import type {PlacementDebugSymbol, TileIdentity, VariantPlacementResultValue} from './placement_debug';
import type {PartPlacementRules, PlacementRules} from './placement_rules';
import type {SymbolSource} from './symbol_source';
import type {SymbolId, SymbolVariantId} from './types';

function noOpOnBlocked() {}

/**
 * Collision grid padding (in pixels) around the viewport. Allows symbols in the
 * viewport to collide with symbols in the padded buffer, improving placement
 * stability during viewport movement.
 *
 * Larger values improve stability at viewport boundaries but increase memory usage
 * and placement computation time with diminishing returns.
 */
const GRID_PADDING = 100;
const GRID_CELL_SIZE = 32;

const INVISIBLE_VARIANTS_COLLISION_PADDING = 1;
const VISIBLE_VARIANTS_COLLISION_PADDING = 0;

/**
 * Combines `styleLayerId` and `symbolIdOrigin` into a single safe-integer map key.
 * Unlike `symbolId` (which may be up to Number.MAX_SAFE_INTEGER) both
 * inputs here are small and bounded, so we can create a key without hashing
 */
function layerOriginKey(id: SymbolId): number {
    assert(id.styleLayerId >= 0 && id.styleLayerId < Number.MAX_SAFE_INTEGER / 2);
    return id.styleLayerId * 2 + id.symbolIdOrigin;
}

function symbolVariantIdEquals(a: SymbolVariantId, b: SymbolVariantId): boolean {
    return a.variantIdx === b.variantIdx &&
        a.symbolId.styleLayerId === b.symbolId.styleLayerId &&
        a.symbolId.symbolIdOrigin === b.symbolId.symbolIdOrigin &&
        a.symbolId.symbolId === b.symbolId.symbolId;
}

function symbolIdEquals(a: SymbolId, b: SymbolId): boolean {
    return a.styleLayerId === b.styleLayerId &&
        a.symbolIdOrigin === b.symbolIdOrigin &&
        a.symbolId === b.symbolId;
}

// layerOriginKey(symbolId) -> symbolId.symbolId -> Set<variantIdx>
type VariantIdSet = Map<number, Map<number, Set<number>>>;

function hasVariantId(set: VariantIdSet, id: SymbolVariantId): boolean {
    const bySymbolId = set.get(layerOriginKey(id.symbolId));
    if (!bySymbolId) return false;
    const byVariantIdx = bySymbolId.get(id.symbolId.symbolId);
    return byVariantIdx !== undefined && byVariantIdx.has(id.variantIdx);
}

function addVariantId(set: VariantIdSet, id: SymbolVariantId) {
    const key = layerOriginKey(id.symbolId);
    let bySymbolId = set.get(key);
    if (!bySymbolId) {
        bySymbolId = new Map();
        set.set(key, bySymbolId);
    }
    let byVariantIdx = bySymbolId.get(id.symbolId.symbolId);
    if (!byVariantIdx) {
        byVariantIdx = new Set();
        bySymbolId.set(id.symbolId.symbolId, byVariantIdx);
    }
    byVariantIdx.add(id.variantIdx);
}

type PartInfo = {
    geometry: Geometry;
    rules: PartPlacementRules;
};

type SymbolInfo = {
    priority: GlobalPlacementPriority;
    source: SymbolSource;
    variantId: SymbolVariantId;
    // Streamed in via addSymbolVariantPart() between startSymbolVariantProcessing() and
    // finishVariantProcessing(). Never empty once finishVariantProcessing() has kept the entry.
    parts: Array<PartInfo>;
    placementRules: PlacementRules;
    tileID: TileIdentity;
    featureId?: string | number;
};

/**
 * Decides symbol visibility purely from a `GlobalPlacementPriority` instead of ayer z-order.
 *
 * Must call `finishPlacementRun()` explicitly before the next `startPlacement` call,
 * `finishSourceProcessing()` before the next `startSymbolSourceProcessing` call, and
 * `finishVariantProcessing()` before the next `startSymbolVariantProcessing` call.
 *
 * Example:
 * ```
 * const placement = new GlobalPlacement();
 * placement.startPlacement(timestamp, screenWidth, screenHeight);
 * for (const layer of layerRenderItems) layer.placeSymbols(placement);
 * placement.finishPlacementRun();
 * ```
 * Later, within a bucket:
 * ```
 * placement.startSymbolSourceProcessing(source);
 * for (const symbolVariant of symbolVariants) {
 *     placement.startSymbolVariantProcessing(...);
 *     for (const part of symbolVariant.parts) placement.addSymbolVariantPart(part.geometry, part.rules);
 *     placement.finishVariantProcessing();
 * }
 * placement.finishSourceProcessing();
 * ```
 */
export class GlobalPlacement {
    _runStarted: boolean;
    _grid: CollisionGrid<SymbolVariantId> | null;
    _timestamp: number;
    _symbols: Array<SymbolInfo>;
    // If set, this variant's onlyIfPlaced must be one of these to be eligible for placement.
    // Only ids actually referenced by an onlyIfPlaced rule are tracked here.
    _onlyIfPlacedReferencedIds: VariantIdSet;
    _placedVariantIds: VariantIdSet;
    // layerOriginKey(symbolId) -> symbolId.symbolId -> the variantIdx that won placement
    _placedSymbolIds: Map<number, Map<number, number>>;
    _processingSource: SymbolSource | null;
    // The variant currently streaming parts in is always the last entry of _symbols.
    _variantProcessingStarted: boolean;
    _collectDebugData: boolean;
    _debugSymbols: Array<PlacementDebugSymbol>;
    // Scratch, valid only for the duration of one _placeSymbolVariant() call. Only ever written
    // when _collectDebugData is true.
    _lastBlockedBy: SymbolVariantId | undefined;

    constructor() {
        this._runStarted = false;
        this._grid = null;
        this._timestamp = 0;
        this._symbols = [];
        this._onlyIfPlacedReferencedIds = new Map();
        this._placedVariantIds = new Map();
        this._placedSymbolIds = new Map();
        this._processingSource = null;
        this._variantProcessingStarted = false;
        this._collectDebugData = false;
        this._debugSymbols = [];
        this._lastBlockedBy = undefined;
    }

    isCollectingDebugData(): boolean {
        return this._collectDebugData;
    }

    startPlacement(timestamp: number, screenWidth: number, screenHeight: number, collectDebugData: boolean = false) {
        if (this._runStarted) throw new Error('Attempt to start a placement run before finishing the previous one');
        this._runStarted = true;

        if (this._grid) {
            this._grid.clearAndResize(screenWidth, screenHeight);
        } else {
            this._grid = new CollisionGrid<SymbolVariantId>(screenWidth, screenHeight, GRID_PADDING, GRID_CELL_SIZE);
        }
        this._timestamp = timestamp;
        this._symbols = [];
        this._onlyIfPlacedReferencedIds.clear();
        this._placedVariantIds.clear();
        this._placedSymbolIds.clear();
        this._collectDebugData = collectDebugData;
        this._debugSymbols = [];
    }

    debugSymbols(): ReadonlyArray<PlacementDebugSymbol> {
        return this._debugSymbols;
    }

    timestamp(): number {
        return this._timestamp;
    }

    _hasPlacedSymbol(id: SymbolId): boolean {
        const bySymbolId = this._placedSymbolIds.get(layerOriginKey(id));
        return bySymbolId !== undefined && bySymbolId.has(id.symbolId);
    }

    _placedVariantIdxFor(id: SymbolId): number | undefined {
        const bySymbolId = this._placedSymbolIds.get(layerOriginKey(id));
        return bySymbolId && bySymbolId.get(id.symbolId);
    }

    _addPlacedSymbol(id: SymbolVariantId) {
        const key = layerOriginKey(id.symbolId);
        let bySymbolId = this._placedSymbolIds.get(key);
        if (!bySymbolId) {
            bySymbolId = new Map();
            this._placedSymbolIds.set(key, bySymbolId);
        }
        bySymbolId.set(id.symbolId.symbolId, id.variantIdx);
    }

    startSymbolSourceProcessing(source: SymbolSource) {
        if (!this._runStarted) throw new Error('Attempt to start symbol source processing outside of a placement run');
        if (this._processingSource) throw new Error('Attempt to start a placement symbol source processing before finishing the previous one');

        this._processingSource = source;
    }

    startSymbolVariantProcessing(variantId: SymbolVariantId, priority: GlobalPlacementPriority, placementRules: PlacementRules, tileID: TileIdentity, featureId?: string | number) {
        const source = this._processingSource;
        if (!source) throw new Error('Attempt to start a symbol variant processing outside of symbol source processing');
        if (this._variantProcessingStarted) throw new Error('Attempt to begin a symbol variant processing before finishing the previous one');

        this._symbols.push({priority, source, variantId, parts: [], placementRules, tileID, featureId});

        if (placementRules.onlyIfPlaced) {
            assert(!symbolIdEquals(placementRules.onlyIfPlaced.symbolId, variantId.symbolId));
            addVariantId(this._onlyIfPlacedReferencedIds, placementRules.onlyIfPlaced);
        }

        this._variantProcessingStarted = true;
    }

    /**
     * Adds one part of the variant started by startSymbolVariantProcessing(): a piece of geometry
     * with the rules it is placed by.
     *
     * All the parts of a variant are placed as a whole: if any of them fails its rules, none of them
     * is shown. Parts of one variant never collide with each other.
     *
     * Geometry is expressed in logical (device-independent) screen pixels, the same space as
     * startPlacement's screenWidth/screenHeight, and must not be empty. It is stored by reference,
     * so callers must not mutate it afterwards.
     */
    addSymbolVariantPart(geometry: Geometry, rules: PartPlacementRules) {
        if (!this._variantProcessingStarted) throw new Error('Attempt to add a symbol variant part outside of symbol variant processing');
        if (geometry.length === 0) throw new Error('Attempt to add a symbol variant part without geometry');
        this._symbols.at(-1)!.parts.push({geometry, rules});
    }

    finishVariantProcessing() {
        if (!this._variantProcessingStarted) throw new Error('Attempt to finish a symbol variant processing that was not started');
        this._variantProcessingStarted = false;

        const symbol = this._symbols.at(-1)!;
        if (symbol.parts.length === 0) {
            // A begun variant can receive no part (every element culled, or dynamically generated
            // geometry that turns out empty). It cannot be placed, so a currently-visible variant has to
            // be hidden here: finishPlacementRun never sees a dropped symbol and would otherwise leave it
            // visible forever without any collision box in the grid.
            if (symbol.priority.symbolVariantVisibility === SymbolVariantVisibility.VARIANT_VISIBLE) {
                symbol.source.hideSymbolVariant(symbol.variantId, this._timestamp);
            }
            this._symbols.pop();
        }
    }

    finishSourceProcessing() {
        this._processingSource = null;
    }

    _placeSymbolVariant(symbol: SymbolInfo, collisionPadding: number): VariantPlacementResultValue {
        const grid = this._grid!;

        this._lastBlockedBy = undefined;

        if (this._hasPlacedSymbol(symbol.variantId.symbolId)) {
            if (this._collectDebugData) {
                const winningVariantIdx = this._placedVariantIdxFor(symbol.variantId.symbolId);
                if (winningVariantIdx !== undefined) this._lastBlockedBy = {symbolId: symbol.variantId.symbolId, variantIdx: winningVariantIdx};
            }
            return VariantPlacementResult.OTHER_VARIANT_PLACED;
        }

        const onlyIfPlaced = symbol.placementRules.onlyIfPlaced;
        if (onlyIfPlaced && !hasVariantId(this._placedVariantIds, onlyIfPlaced)) {
            return VariantPlacementResult.DEPENDENCY_NOT_PLACED;
        }

        const onBlocked = this._collectDebugData ? (data: SymbolVariantId) => { this._lastBlockedBy = data; } : noOpOnBlocked;

        // The variant is placed as a whole, so we do not want to insert its parts right away: inserted
        // parts would collide with the parts checked after them.
        let anyPartChecked = false;
        let anyCheckedPartInGrid = false;
        for (const part of symbol.parts) {
            const collisionRules = part.rules.collisionRules;
            if (!collisionRules) continue;
            anyPartChecked = true;
            const ignoreVariantId = collisionRules.symbolVariantToIgnoreCollisionWith;
            const intersectionResult = grid.intersects(
                part.geometry,
                collisionPadding,
                (data) => ignoreVariantId !== undefined && symbolVariantIdEquals(data, ignoreVariantId),
                onBlocked
            );
            if (intersectionResult === 'intersects') return VariantPlacementResult.COLLIDED;
            if (intersectionResult === 'does-not-intersect') anyCheckedPartInGrid = true;
        }
        // Out of bounds only if the whole variant is: a part inside the grid keeps the rest of it placeable.
        if (anyPartChecked && !anyCheckedPartInGrid) return VariantPlacementResult.OUT_OF_BOUNDS;

        let anyPartInserted = false;
        let anyPartNotInserted = false;
        for (const part of symbol.parts) {
            if (!part.rules.insertIntoCollisionGrid) continue;
            // insert() only fails when every element lies outside the grid.
            // gl-native has a geometry cap and can also return TooManyGeometries but we don't have that in GL JS.
            if (grid.insert(part.geometry, symbol.variantId)) anyPartInserted = true;
            else anyPartNotInserted = true;
        }
        // A variant whose parts all opt out of the grid is placed without ever touching it.
        if (anyPartNotInserted && !anyPartInserted) return VariantPlacementResult.OUT_OF_BOUNDS;

        return VariantPlacementResult.PLACED;
    }

    finishPlacementRun() {
        if (!this._runStarted) throw new Error('Attempt to finish a placement run that was not started');
        this._runStarted = false;

        const timestamp = this._timestamp;

        this._symbols.sort((a, b) => comparePriority(b.priority, a.priority));

        for (const symbol of this._symbols) {
            const wasVisible = symbol.priority.symbolVariantVisibility === SymbolVariantVisibility.VARIANT_VISIBLE;
            const collisionPadding = wasVisible ? VISIBLE_VARIANTS_COLLISION_PADDING : INVISIBLE_VARIANTS_COLLISION_PADDING;

            const status = this._placeSymbolVariant(symbol, collisionPadding);
            const visible = status === VariantPlacementResult.PLACED;

            if (visible) {
                this._addPlacedSymbol(symbol.variantId);
                if (hasVariantId(this._onlyIfPlacedReferencedIds, symbol.variantId)) addVariantId(this._placedVariantIds, symbol.variantId);
            }

            if (wasVisible !== visible) {
                if (visible) symbol.source.showSymbolVariant(symbol.variantId, timestamp);
                else symbol.source.hideSymbolVariant(symbol.variantId, timestamp);
            }

            if (this._collectDebugData) {
                const displayedPadding = visible ? VISIBLE_VARIANTS_COLLISION_PADDING : collisionPadding;
                // Parts of a variant are streamed in one after another, so their geometry elements
                // are recorded in that same order here.
                const elements: Array<GeometryElement> = [];
                for (const part of symbol.parts) elements.push(...part.geometry);
                this._debugSymbols.push({
                    geometry: elements,
                    collisionPadding: displayedPadding,
                    variantId: symbol.variantId,
                    tileID: symbol.tileID,
                    featureId: symbol.featureId,
                    placementRules: symbol.placementRules,
                    partPlacementRules: symbol.parts.map((part) => part.rules),
                    status,
                    blockedBy: this._lastBlockedBy,
                });
            }
        }
    }
}
